"use client";

import { useState, useRef, useEffect } from "react";
import { AnimatePresence, motion } from "motion/react";
import { useTranslations } from "next-intl";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import SoundSettings from "@/components/ui/SoundSettings/SoundSettings";
import { useSound } from "@/contexts/SoundContext";

// Bouton + popover de réglage du son, partagé entre les leçons Reps et les quiz
// pour une barre identique. Source unique = SoundContext (on/off + volume,
// persistés côté compte). Le panneau réutilise le composant SoundSettings.
export default function SoundSettingsPopover() {
    const t = useTranslations();
    const { masterVolume, soundEnabled, bebotVoiceEnabled } = useSound();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        function onDoc(e) {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        }
        function onEsc(e) {
            if (e.key === "Escape") setOpen(false);
        }
        document.addEventListener("mousedown", onDoc);
        document.addEventListener("keydown", onEsc);
        return () => {
            document.removeEventListener("mousedown", onDoc);
            document.removeEventListener("keydown", onEsc);
        };
    }, [open]);

    // Muet si le général est à zéro, ou si effets ET voix sont coupés
    const allOff =
        masterVolume <= 0 || (!soundEnabled && !bebotVoiceEnabled);
    const icon = allOff
        ? "muted-linear"
        : masterVolume < 0.34
          ? "volume-small-linear"
          : "volume-loud-linear";

    return (
        <div className="relative shrink-0" ref={ref}>
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                aria-label={t("sound_settings")}
                className="flex text-gray-400 transition-colors hover:text-gray-700 dark:hover:text-white"
            >
                <SolarIcon name={icon} className="text-xl" />
            </button>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.96, y: -4 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.96, y: -4 }}
                        transition={{ duration: 0.15 }}
                        className="absolute right-0 top-full z-[70] mt-2 w-60 rounded-2xl border border-gray-200 bg-white p-4 shadow-xl dark:border-white/10 dark:bg-bgDark-840"
                    >
                        <p className="mb-3 text-[11px] font-bold uppercase tracking-wider text-gray-400 dark:text-grayDark-500">
                            {t("sound")}
                        </p>
                        <SoundSettings />
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
